<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card beauty-card">
      <div class="table-toolbar">
       
        <div class="right">
          <el-button size="mini" icon="el-icon-refresh" :loading="loading && !polling" @click="fetchList()">刷新</el-button>
        </div>
      </div>

      <el-table
        v-loading="loading && !polling"
        :data="taskList"
        class="beauty-table"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
      >
        <el-table-column prop="id" label="ID" width="80" align="center" />
        <el-table-column prop="fileName" label="文件名" min-width="200" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="160" align="center">
          <template slot-scope="{ row }">
            <div>
              <el-tag :type="statusType(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
              <span v-if="row.status === 'processing' && (row.totalCount > 0 || row.processedCount > 0)" class="progress-text">
                ({{ row.processedCount || 0 }}/{{ row.totalCount || 0 }})
              </span>
              <el-progress
                v-if="row.status === 'processing' && row.totalCount > 0"
                :percentage="calcTaskPercent(row)"
                :stroke-width="6"
                :show-text="false"
                style="margin-top: 6px;"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="170" align="center" />
        <el-table-column prop="completedAt" label="完成时间" width="170" align="center" />
        <el-table-column prop="errorMsg" label="失败原因" min-width="150" show-overflow-tooltip />
        <el-table-column label="操作" align="center" fixed="right" width="100">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.status === 'completed'"
              size="mini"
              type="text"
              icon="el-icon-download"
              :loading="row._downloading"
              @click="handleDownload(row)"
            >下载</el-button>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          :current-page="query.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="query.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { listDownloadTasks, buildDownloadTaskFileUrl } from '@/api/material/material';

export default {
  name: 'DownloadTasks',
  data() {
    return {
      loading: false,
      polling: false,
      fetching: false,
      taskList: [],
      total: 0,
      query: {
        pageNo: 1,
        pageSize: 10
      },
      pollTimer: null,
      /** 连续轮询失败次数，用于退避，避免后端抖动时狂弹 502 */
      pollFailCount: 0
    };
  },
  mounted() {
    this.fetchList();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    /**
     * @param {object} [options]
     * @param {boolean} [options.silent] 轮询时静默：不打全屏 loading、不弹全局/本地错误（防 502 叠层）
     */
    async fetchList(options = {}) {
      const silent = !!options.silent;
      if (this.fetching) {
        return;
      }
      this.fetching = true;
      if (!silent) {
        this.loading = true;
      } else {
        this.polling = true;
      }
      try {
        const res = await listDownloadTasks(
          {
            pageNo: this.query.pageNo,
            pageSize: this.query.pageSize
          },
          silent ? { skipErrorMessage: true } : {}
        );
        const page = res.data || res;
        this.taskList = page.records || [];
        this.total = page.total || 0;
        const hadFailures = this.pollFailCount > 0;
        this.pollFailCount = 0;

        const hasProcessing = this.taskList.some(t => t.status === 'pending' || t.status === 'processing');
        if (hasProcessing) {
          // 失败退避后恢复成功时，重建定时器以回到 3s 间隔
          if (hadFailures) {
            this.restartPolling();
          } else {
            this.startPolling();
          }
        } else {
          this.stopPolling();
        }
      } catch (e) {
        if (silent) {
          this.pollFailCount += 1;
          // 连续失败时拉长轮询间隔，减轻对已不稳定后端的压力
          if (this.pollFailCount >= 2) {
            this.restartPolling();
          }
        } else {
          this.$message.error('加载失败：' + (e && e.message ? e.message : '未知错误'));
        }
      } finally {
        this.fetching = false;
        this.loading = false;
        this.polling = false;
      }
    },
    startPolling() {
      if (this.pollTimer) {
        return;
      }
      this.pollTimer = setInterval(() => {
        this.fetchList({ silent: true });
      }, this.pollIntervalMs());
    },
    restartPolling() {
      this.stopPolling();
      this.startPolling();
    },
    pollIntervalMs() {
      // 正常 3s；失败后最多退避到 15s
      const n = Math.min(this.pollFailCount, 4);
      return 3000 + n * 3000;
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    handleSizeChange(val) {
      this.query.pageSize = val;
      this.query.pageNo = 1;
      this.fetchList();
    },
    handleCurrentChange(val) {
      this.query.pageNo = val;
      this.fetchList();
    },
    statusText(status) {
      const map = { pending: '等待中', processing: '打包中', completed: '已完成', failed: '失败' };
      return map[status] || status;
    },
    statusType(status) {
      const map = { pending: 'info', processing: 'warning', completed: 'success', failed: 'danger' };
      return map[status] || 'info';
    },
    calcTaskPercent(row) {
      const total = Number(row && row.totalCount) || 0;
      const processed = Number(row && row.processedCount) || 0;
      if (total <= 0) return 0;
      return Math.max(0, Math.min(100, Math.round((processed / total) * 100)));
    },
    handleDownload(row) {
      if (row._downloading) return;
      const token = localStorage.getItem('token');
      if (!token) {
        this.$message.error('登录已失效，请重新登录');
        return;
      }
      // 交给浏览器原生下载：可走流式、有下载栏进度，不再 axios 整包进内存干等
      this.$set(row, '_downloading', true);
      try {
        const url = buildDownloadTaskFileUrl(row.id);
        const a = document.createElement('a');
        a.href = url;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.$message.success('已开始下载，请查看浏览器底部下载栏');
      } catch (e) {
        this.$message.error('下载失败：' + (e && e.message ? e.message : '未知错误'));
      } finally {
        // 原生下载由浏览器接管，按钮不必一直转圈
        setTimeout(() => {
          this.$set(row, '_downloading', false);
        }, 600);
      }
    }
  }
};
</script>

<style scoped>
.page-title {
  font-weight: 600;
  color: #303133;
}
.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.muted {
  color: #c0c4cc;
}
.progress-text {
  margin-left: 4px;
  font-size: 12px;
  color: #909399;
}
</style>
